import React, { useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  validateForm,
  validateMinimumFields,
  validatePendingActivation,
  hasValidationErrors,
  validateActivateLease,
} from "../../../utils/formValidation";
import {
  setValidationErrors,
  setShowValidationMessage,
} from "../../../redux/slices/formValidationSlice";
import { useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "config/config";
import { postApiCall } from "services/apiHelper";
import { allSelectedData } from "../../../redux/slices/allSelectedDataSlice";
import { useResetReduxState } from "hooks/common-hooks";
import CommentPopup from "../../util/CommentPopup";
import { Button, Stack, Box, CircularProgress } from "@mui/material";
import { setShowSuccessMessage } from "../../../redux/slices/formValidationSlice";
import { LoadingButton } from "@mui/lab";

const ActionButtons: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedData = useAppSelector((state) => state.allSelectedData);
  const userContext = useAppSelector((state) => state.userContextDetails?.data);
  const userRole = useAppSelector((state) =>
    state.userContextDetails?.data[0]?.role.toLowerCase()
  );
  const navigate = useNavigate();
  const resetReduxState = useResetReduxState();
  const { formtype, itemid } = useParams();
  const status = useAppSelector((state) => state.allSelectedData.leaseStatus);
  const isLeaseActive = useAppSelector(
    (state) => state.allSelectedData.isActive
  );
  const [commentPopupOpen, setCommentPopupOpen] = useState(false);
  const [pendingActionType, setPendingActionType] = useState("");
  const [buttonStatus, setButtonStatus] = useState(status);

  const isAdmin = userRole === "admin";
  const isPrM =
    (userRole === "property manager" ||
    "assistant property manager" ||
    "property mgmt accountant");
  const isAccenture = userRole === "accenture";
  const isLeaseClosed = isLeaseActive === "closed";
  const [loading, setLoading] = useState(false);

  const handleAction = async (
    actionType: string,
    useFullValidation = false,
    comments = ""
  ) => {
    const errors = useFullValidation
      ? validateForm(selectedData)
      : validateMinimumFields(selectedData);
    dispatch(setValidationErrors(errors));
    if (hasValidationErrors(errors)) {
      dispatch(setShowValidationMessage(true));
      return;
    }
    setLoading(true);
    const currentDateTime = new Date().toISOString();
    const updatedData = {
      ...selectedData,
      lastModified: currentDateTime,
      lastModifiedBy: userContext[0].emailId,
      isActive: "InProgress",
      leaseStatus: actionType,
      created: formtype === "new" ? currentDateTime : selectedData.created,
      createdBy:
        formtype === "new" ? userContext[0].emailId : selectedData.createdBy,
      leasePopupUpdateComments: comments,
    };
    if (actionType === "Lease Activated") {
      updatedData.isActive = "Active";
    }
    const url =
      formtype === "edit" || formtype === "approval"
        ? `${baseUrl}Leases/update`
        : `${baseUrl}Leases/add`;
    try {
      await postApiCall(url, updatedData).then(() => {
        dispatch(setShowSuccessMessage(true));
        if (actionType !== "Abstraction in Progress") {
          setTimeout(() => {
            dispatch(setShowSuccessMessage(false));
            resetReduxState();
            setLoading(false);
            navigate("/lease");
          }, 4000);
        } else if (actionType === "Abstraction in Progress") {
          dispatch(allSelectedData(updatedData));
          setLoading(false);
          setButtonStatus(actionType);
          setTimeout(() => {
            dispatch(setShowSuccessMessage(false));
          }, 4000);
        }
      });
    } catch (error) {
      console.error("API call failed:", error);
    }
  };

  const handleEdit = () => navigate(`/lease/form/edit/${itemid}`);
  const handleClose = () => navigate("/lease");

  const getButtonConfig = () => {
    if (isLeaseClosed)
      return {
        leftButtons: [
          { label: "Close", onClick: handleClose, variant: "outlined" },
        ],
      };

    switch (formtype) {
      case "new":
      case "edit":
        if (["Saved", ""].includes(buttonStatus) && (isPrM || isAdmin)) {
          return {
            leftButtons: [
              {
                label: "Save",
                onClick: () => handleAction("Saved"),
                variant: "contained",
              },
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              {
                label: "Submit to Accenture",
                onClick: () => handleAction("Pending Abstraction", true),
                variant: "contained",
                color: "success",
              },
            ],
          };
        }
        if (
          ["Pending PrM Updates"].includes(buttonStatus) &&
          (isPrM || isAdmin)
        ) {
          return {
            leftButtons: [
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              {
                label: "Submit to Accenture",
                onClick: () => handleAction("Pending Abstraction", true),
                variant: "contained",
                color: "success",
              },
            ],
          };
        }
        if (
          isAdmin &&
          buttonStatus !== "Saved" &&
          buttonStatus !== "Lease Activated"
        ) {
          return {
            leftButtons: [
              {
                label: "Save",
                onClick: () => handleAction("Saved"),
                variant: "contained",
              },
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
          };
        }
        break;

      case "view":
        return {
          leftButtons: [
            ...((isPrM && buttonStatus === "Saved") ||
            (isAdmin && buttonStatus !== "Lease Activated")
              ? [
                  {
                    label: "Edit",
                    onClick: handleEdit,
                    variant: "contained",
                    color: "success",
                  },
                ]
              : []),
            { label: "Close", onClick: handleClose, variant: "outlined" },
          ],
        };

      case "approval":
        if (buttonStatus === "Pending Activation" && (isAccenture || isAdmin)) {
          return {
            centerButtons: [
              {
                label: "Confirm Lease Abstraction",
                onClick: () => {
                  const errors = validatePendingActivation(selectedData);
                  dispatch(setValidationErrors(errors));
                  if (hasValidationErrors(errors)) {
                    dispatch(setShowValidationMessage(true));
                    return;
                  }
                  handleAction("Lease Activated");
                },
                variant: "contained",
              },
            ],
          };
        }
        if (
          ["Pending Abstraction"].includes(buttonStatus) &&
          (isAccenture || isAdmin)
        ) {
          return {
            leftButtons: [
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              ...(buttonStatus === "Pending Abstraction"
                ? [
                    {
                      label: "Initiate Abstraction",
                      onClick: () => handleAction("Abstraction in Progress"),
                      variant: "contained",
                    },
                  ]
                : []),
              {
                label: "Request Updates",
                onClick: () => {
                  setCommentPopupOpen(true);
                  setPendingActionType("Pending PrM Updates");
                },
                variant: "contained",
              },
              ...(buttonStatus === "Abstraction in Progress"
                ? [
                    {
                      label: "Submit to PrM",
                      onClick: () => {
                        const errors = validateActivateLease(selectedData);
                        dispatch(setValidationErrors(errors));
                        if (hasValidationErrors(errors)) {
                          dispatch(setShowValidationMessage(true));
                          return;
                        }
                        handleAction("Pending PrM Review");
                      },
                      variant: "contained",
                    },
                  ]
                : [
                    {
                      label: "Submit to PrM",
                      variant: "contained",
                      disabled: true,
                    },
                  ]),
            ],
          };
        }

        if (
          ["Abstraction in Progress"].includes(buttonStatus) &&
          (isAccenture || isAdmin)
        ) {
          return {
            leftButtons: [
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              {
                label: "Request Updates",
                onClick: () => {
                  setCommentPopupOpen(true);
                  setPendingActionType("Pending PrM Updates");
                },
                variant: "contained",
              },
              ...(buttonStatus === "Abstraction in Progress"
                ? [
                    {
                      label: "Submit to PrM",
                      onClick: () => {
                        const errors = validateActivateLease(selectedData);
                        dispatch(setValidationErrors(errors));
                        if (hasValidationErrors(errors)) {
                          dispatch(setShowValidationMessage(true));
                          return;
                        }
                        handleAction("Pending PrM Review");
                      },
                      variant: "contained",
                    },
                  ]
                : [{ label: "Submit to PrM", variant: "contained" }]),
            ],
          };
        }

        if (
          buttonStatus === "Pending Accenture Updates" &&
          (isAccenture || isAdmin)
        ) {
          return {
            leftButtons: [
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              {
                label: "Submit to PrM",
                onClick: () => handleAction("Pending PrM Review"),
                variant: "contained",
              },
            ],
          };
        }
        if (buttonStatus === "Pending PrM Updates" && (isPrM || isAdmin)) {
          return {
            leftButtons: [
              {
                label: "Edit",
                onClick: handleEdit,
                variant: "contained",
                color: "success",
              },
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              {
                label: "Submit to Accenture",
                onClick: () => handleAction("Pending Abstraction"),
                variant: "contained",
              },
            ],
          };
        }
        if (buttonStatus === "Pending PrM Review" && (isPrM || isAdmin)) {
          return {
            leftButtons: [
              { label: "Close", onClick: handleClose, variant: "outlined" },
            ],
            rightButtons: [
              {
                label: "Request Updates",
                onClick: () => {
                  setCommentPopupOpen(true);
                  setPendingActionType("Pending Accenture Updates");
                },
                variant: "contained",
              },
              {
                label: "Activate Lease",
                onClick: () => {
                  handleAction("Pending Activation");
                },
                variant: "contained",
                color: "success",
              },
            ],
          };
        }
        break;
    }

    return {
      leftButtons: [
        { label: "Close", onClick: handleClose, variant: "outlined" },
      ],
    };
  };

  const {
    leftButtons = [],
    rightButtons = [],
    centerButtons = [],
  } = getButtonConfig();

  const renderButton = (btn: any, key: number) => (
    <LoadingButton
      key={key}
      variant={btn.variant}
      color={btn.color || "primary"}
      onClick={btn.onClick}
      disabled={btn.disabled}
      loading={loading}
      sx={
        btn.variant === "outlined" ? { borderColor: "#666", color: "#666" } : {}
      }
    >
      {btn.label}
    </LoadingButton>
  );

  if (centerButtons.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        width="100%"
        sx={{ marginBottom: "80px" }}
      >
        <Stack direction="row" spacing={2}>
          {centerButtons.map(renderButton)}
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        alignItems="center"
        sx={{ marginBottom: "55px" }}
      >
        <Stack direction="row" spacing={2}>
          {leftButtons.map(renderButton)}
        </Stack>
        {rightButtons.length > 0 && (
          <Stack direction="row" spacing={2}>
            {rightButtons.map(renderButton)}
          </Stack>
        )}
      </Box>
      <CommentPopup
        open={commentPopupOpen}
        onClose={() => setCommentPopupOpen(false)}
        onConfirm={(comment) => handleAction(pendingActionType, false, comment)}
      />
    </>
  );
};

export default ActionButtons;
